import { create } from "zustand";
import { Catalog, Component } from "./types";

/**
 * Reactive store for the active journey-run surface.
 *
 * dataModel is keyed by absolute path strings (e.g. "/form/email").
 * Reads use longest-prefix-match so "/data/users/record/email_id" can
 * drill into the value at "/data/users/record".
 *
 * Components subscribe to the paths they care about — Zustand's selector
 * mechanism means typing into one TextField doesn't re-render the whole tree.
 */

/** Identity of the streamed run state (the surfaceMeta frame payload). */
export interface SurfaceMeta {
  surfaceId: string;
  catalogVersion: string;
  runId: string;
  journeyId: string;
}

interface RendererState {
  // App identity
  appName: string;
  surfaceId: string | null;
  rootComponentId: string | null;

  // Active journey run (from the surfaceMeta frame) — bex.act targets it
  runId: string | null;
  journeyId: string | null;

  // Catalog (versioned, loaded with each run state)
  catalogVersion: string | null;
  catalog: Catalog | null;

  // Components, indexed by id
  components: Map<string, Component>;

  // Reactive data model
  dataModel: Record<string, unknown>;
  // Bumped on every dataModel write — components subscribed via selectors
  // re-evaluate against the new state.
  modelRevision: number;

  // Load-time errors (per entity) and renderer-level error chrome
  loadErrors: Record<string, string>;
  fatalError: string | null;

  // In-flight backend calls (open / action / sign-out). >0 ⇒ a request is
  // outstanding — drives the global loading indicator so the user gets
  // feedback during the (Mongo-bound) round-trips instead of a frozen screen.
  pending: number;

  // True after an OPTIMISTIC render: the screen STRUCTURE is shown instantly
  // from the cached bundle while its data is still loading — data components
  // render skeletons until the real frames land. Suppresses the top load bar
  // (the skeleton IS the indicator).
  pendingData: boolean;

  // Actions
  setAppName: (n: string) => void;
  beginPending: () => void;
  endPending: () => void;
  applyCatalog: (c: Catalog) => void;
  applySurfaceUpdate: (surfaceId: string, components: Component[]) => void;
  applyDataModelUpdate: (patch: Record<string, unknown>) => void;
  applyLoadErrors: (errs: Record<string, string>) => void;
  setRootComponent: (id: string) => void;
  setSurfaceMeta: (meta: SurfaceMeta) => void;
  applyLocalSurface: (
    meta: { surfaceId: string; catalogVersion?: string },
    components: Component[],
    rootComponentId: string,
  ) => void;
  applyOptimisticSurface: (
    meta: { surfaceId: string; catalogVersion?: string },
    components: Component[],
    rootComponentId: string,
  ) => void;
  setFatalError: (msg: string | null) => void;
  resetSurface: () => void;
  writeDataPath: (path: string, value: unknown) => void;
}

export const useStore = create<RendererState>((set) => ({
  appName: "",
  surfaceId: null,
  rootComponentId: null,
  runId: null,
  journeyId: null,
  catalogVersion: null,
  catalog: null,
  components: new Map(),
  dataModel: {},
  modelRevision: 0,
  loadErrors: {},
  fatalError: null,
  pending: 0,
  pendingData: false,

  setAppName: (n) => set({ appName: n }),
  beginPending: () => set((s) => ({ pending: s.pending + 1 })),
  endPending: () => set((s) => ({ pending: Math.max(0, s.pending - 1) })),
  applyCatalog: (c) => set({ catalog: c, catalogVersion: c.version }),
  applySurfaceUpdate: (surfaceId, components) => {
    const map = new Map<string, Component>();
    for (const c of components) map.set(c.id, c);
    set({ surfaceId, components: map });
  },
  applyDataModelUpdate: (patch) =>
    set((s) => ({ dataModel: { ...s.dataModel, ...patch },
                  modelRevision: s.modelRevision + 1,
                  pendingData: false })),   // real data landed — skeletons resolve
  applyLoadErrors: (errs) => set({ loadErrors: errs }),
  setRootComponent: (id) => set({ rootComponentId: id }),
  setSurfaceMeta: (meta) =>
    set((s) => {
      // Moving to a different surface ⇒ stale form input must not leak into
      // the next surface's /form bindings; reset it before the new frames'
      // dataModelUpdate (which carries the authoritative ctx.form) merges in.
      const surfaceChanged = s.surfaceId !== null && s.surfaceId !== meta.surfaceId;
      const base = {
        surfaceId: meta.surfaceId,
        catalogVersion: meta.catalogVersion,
        runId: meta.runId,
        journeyId: meta.journeyId,
      };
      if (!surfaceChanged) return base;
      const dataModel: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(s.dataModel)) {
        if (k !== "/form" && !k.startsWith("/form/")) dataModel[k] = v;
      }
      return { ...base, dataModel, modelRevision: s.modelRevision + 1 };
    }),
  // Local (FE-driven) navigation hop: swap to a cached surface WITHOUT a server
  // round-trip. The target is an FE-safe screen (no reads), so the existing
  // dataModel (/session, /app/menu, /form) stays valid and is preserved — no
  // server dataModelUpdate will arrive to repopulate it. runId/journeyId are
  // unchanged (a local hop stays within the same run + journey).
  applyLocalSurface: (meta, components, rootComponentId) =>
    set((s) => {
      const map = new Map<string, Component>();
      for (const c of components) map.set(c.id, c);
      return {
        surfaceId: meta.surfaceId,
        catalogVersion: meta.catalogVersion ?? s.catalogVersion,
        components: map,
        rootComponentId,
        pendingData: false,                   // a no-reads hop has no pending data
        modelRevision: s.modelRevision + 1,   // force bindings to re-resolve
      };
    }),
  // Optimistic hop: show a known surface's STRUCTURE now (from the bundle),
  // dropping stale /data + /result so data components render skeletons, while
  // the real data is fetched. The server frames then fill the data (and correct
  // the surface if it diverged) and clear pendingData.
  applyOptimisticSurface: (meta, components, rootComponentId) =>
    set((s) => {
      const map = new Map<string, Component>();
      for (const c of components) map.set(c.id, c);
      // Drop stale /data + /result (skeletons until the real data lands) AND
      // stale /form + /form/* — this is a move to a different surface, so the
      // prior surface's form inputs must not leak; the server's frames re-send
      // the authoritative ctx.form. (setSurfaceMeta's own wipe is skipped here
      // because the optimistic hop already advanced s.surfaceId.)
      const dataModel: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(s.dataModel)) {
        if (k.startsWith("/data/") || k.startsWith("/result/")) continue;
        if (k === "/form" || k.startsWith("/form/")) continue;
        dataModel[k] = v;
      }
      return {
        surfaceId: meta.surfaceId,
        catalogVersion: meta.catalogVersion ?? s.catalogVersion,
        components: map,
        rootComponentId,
        dataModel,
        pendingData: true,
        modelRevision: s.modelRevision + 1,
      };
    }),
  setFatalError: (msg) => set({ fatalError: msg }),
  resetSurface: () =>
    set({
      surfaceId: null,
      rootComponentId: null,
      runId: null,
      journeyId: null,
      components: new Map(),
      dataModel: {},
      modelRevision: 0,
      loadErrors: {},
      fatalError: null,
      pendingData: false,
    }),
  writeDataPath: (path, value) =>
    set((s) => ({ dataModel: { ...s.dataModel, [path]: value }, modelRevision: s.modelRevision + 1 })),
}));
